import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Input,
  Row,
  Select,
  Space,
  TimePicker,
  Typography,
} from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './style.less';
import { useMediaQuery } from 'react-responsive';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { keys } from 'lodash-es';
import {
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  PullRequestOutlined,
  ThunderboltFilled,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment, { isMoment } from 'moment';

const { Option } = Select;

const LogicsPanel = ({
  questions,
  selectedQuestion,
  updateProperties,
  visible,
  setVisible,
  sectionOrder,
}) => {
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { t } = useTranslation(['builder', 'translation']);

  const headText = useMemo(() => {
    let text = `${t('builder:logicsPanel.title')}`;
    if (selectedQuestion && selectedQuestion.type) {
      if (selectedQuestion.type === 'SECTION') {
        text =
          text +
          ' ' +
          `"${
            selectedQuestion.label ||
            `Page ${sectionOrder.indexOf(selectedQuestion.questionId) + 1}`
          }"`;
      } else {
        text = text + ' ' + `"${selectedQuestion.label}"`;
      }
    }
    return text;
  }, [selectedQuestion, t, sectionOrder]);

  return (
    <div className="Logics-panel">
      <Drawer
        title={
          <Typography.Text ellipsis className="sub-title" style={{ maxWidth: 380 }}>
            {headText}
          </Typography.Text>
        }
        placement="right"
        // closable={true}
        visible={selectedQuestion && visible}
        onClose={setVisible}
        getContainer={false}
        mask={false}
        width={isMobile ? '100%' : 550}
        className="fields-panel"
        // style={{ position: 'fixed', top: isDesktop ? 140 : 188 }}
        headerStyle={{ padding: '20px 20px 16px' }}
        contentWrapperStyle={{
          boxShadow: !isDesktop ? 'rgba(0, 0, 0, 0.16) 0px 1px 4px' : 'none',
          borderLeft: !isDesktop ? 'none' : '2px solid #ecebee',
        }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        {/* {renderLogicFields()} */}
        <FieldLogic
          key={selectedQuestion?.questionId + visible}
          questions={questions}
          question={selectedQuestion}
          update={updateProperties}
        />
        {/* <Button onClick={handleSave} type="primary" style={{ marginTop: 16 }}>
          Save
        </Button> */}
      </Drawer>
    </div>
  );
};

const FieldLogic = ({ questions, question, update }) => {
  const { t } = useTranslation(['builder', 'translation']);
  const methods = useForm({
    mode: 'onChange',
    defaultValues: (() => {
      if (!question) return {};
      let rules = question?.rules || [];
      if (question?.type === 'RADIO') {
        // Get existing rules mapped by valueId
        const existingRules = rules.reduce((acc, rule) => {
          acc[rule.valueId] = rule;
          return acc;
        }, {});

        // Create rules for all options, preserving existing rules
        rules = question?.options?.map((option) => ({
          type: 'EQUALS',
          valueId: option._id,
          jumpToSection: existingRules[option._id]?.jumpToSection || '',
        }));
      } else {
        const noRules = rules.length === 0;
        if (noRules) {
          rules = [{ type: 'EQUALS', value: '', jumpToSection: '' }];
        }
      }
      return { ...question, rules };
    })(),
  });

  const { control, getValues, setValue, watch, register } = methods;

  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: 'rules',
  });

  const watchTypes = watch(fields?.map((_, i) => `rules.${i}.type`));
  const [watchDatePickerMode] = watch(['datePickerMode']);

  const handleAddRule = () => {
    const value = question?.type === 'CHECKBOX' ? [] : '';
    append({ type: 'EQUALS', value, jumpToSection: '' });
  };

  const saveRules = (key, value) => {
    const updatedLogics = getValues('rules');
    update({ rules: updatedLogics });
  };

  const renderLogicFields = () => {
    if (!question) return null;

    switch (question.type) {
      case 'SECTION':
        return (
          <div className="form-group">
            <label className="form-group-label">
              {t('builder:propertiesPanel.section.goToSection', {
                label:
                  question.label ||
                  `Page ${
                    questions
                      .filter((q) => q.type === 'SECTION')
                      .findIndex((q) => q.questionId === question.questionId) + 1
                  }`,
              })}
            </label>
            <Controller
              name="jumpToSection"
              control={control}
              render={({ field }) => (
                <Select
                  style={{ width: '100%' }}
                  {...field}
                  value={field.value || null}
                  placeholder={t('builder:logicsPanel.text.selectAPage')}
                  onChange={(value) => {
                    field.onChange(value);
                    update({ [field.name]: value });
                  }}
                  notFoundContent={
                    <div
                      className="flex"
                      style={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '150px',
                        textAlign: 'center',
                        gap: 12,
                      }}
                    >
                      <WarningOutlined style={{ fontSize: 24 }} />
                      <Typography.Text type="secondary">
                        No Page found. <br />
                        Create a new Page from <b>Advanced</b> {'>'} <b>Page</b>.
                      </Typography.Text>
                    </div>
                  }
                >
                  {/* <Option value="">{t('builder:logicsPanel.section.goToNextPage')}</Option> */}
                  {questions
                    .filter((q) => q.type === 'SECTION')
                    .map((section, idx) => {
                      if (section.questionId === question.questionId) return null;
                      return (
                        <Option key={section.questionId} value={section.questionId}>
                          {t('builder:logicsPanel.section.goToLabel', {
                            label: section.label || `Page ${idx + 1}`,
                          })}
                        </Option>
                      );
                    })}
                  <Option value="END">Submit Form</Option>
                </Select>
              )}
            />
          </div>
        );
      case 'TEXT':
      case 'EMAIL':
      case 'PHONE':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={14}>
                    <div className="form-group">
                      {index === 0 && (
                        <label title={t('builder:logicsPanel.text.ifTheAnswer')}>
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {t('builder:logicsPanel.text.ifTheAnswer')}
                        </label>
                      )}
                      <Row gutter={8}>
                        <Col span={12}>
                          <Controller
                            name={`rules.${index}.type`}
                            control={control}
                            defaultValue="EQUALS"
                            render={({ field }) => (
                              <Select
                                style={{ width: '100%' }}
                                {...field}
                                onChange={(value) => {
                                  field.onChange(value);
                                  saveRules(field.name, value);
                                }}
                              >
                                <Option value="CONTAINS">
                                  {t('builder:logicsPanel.text.contains')}
                                </Option>
                                <Option value="NOT_CONTAINS">
                                  {t('builder:logicsPanel.text.doesNotContain')}
                                </Option>
                                <Option value="EQUALS">
                                  {t('builder:logicsPanel.text.isEqual')}
                                </Option>
                                <Option value="IS_EMPTY">
                                  {t('builder:logicsPanel.text.isEmpty')}
                                </Option>
                                <Option value="IS_NOT_EMPTY">
                                  {t('builder:logicsPanel.text.isNotEmpty')}
                                </Option>
                              </Select>
                            )}
                          />
                        </Col>
                        {!['IS_EMPTY', 'IS_NOT_EMPTY'].includes(watchTypes[index]) && (
                          <Col span={12}>
                            <Controller
                              name={`rules.${index}.value`}
                              control={control}
                              render={({ field }) => (
                                <Input
                                  // size="large"
                                  placeholder={t('builder:logicsPanel.text.enterValue')}
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    saveRules(field.name, e.target.value);
                                  }}
                                />
                              )}
                            />
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                  <Col span={10}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      remove={remove}
                      colSpan={16}
                    />
                  </Col>
                </Row>
              </div>
            ))}

            <Button onClick={handleAddRule} icon={<PlusOutlined />}>
              {t('builder:logicsPanel.text.addRule')}
            </Button>

            {/* <Divider />

            <div className="form-group">
              <label>
                <ThunderboltOutlined style={{ marginRight: 6 }} />
                For all other cases, go to
              </label>
              <Row>
                <Col span={10}>
                  <Select
                    style={{ width: '100%' }}
                    // {...field}
                    // value={field.value || null}
                    placeholder={t('builder:logicsPanel.text.selectAPage')}
                    // onChange={(value) => {
                    //   field.onChange(value);
                    //   saveChanges(field.name, value);
                    // }}
                  >
                    {questions
                      .filter((q) => q.type === 'SECTION')
                      .map((section, idx) => (
                        <Option key={section.questionId} value={section.questionId}>
                          {section.label || `Page ${idx + 1}`}
                        </Option>
                      ))}
                  </Select>
                </Col>
              </Row>
            </div> */}
          </>
        );
      case 'RADIO':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id + index} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={12}>
                    <div className="form-group">
                      {index === 0 && (
                        <label>
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {question?.assignValues
                            ? t('builder:logicsPanel.text.ifTheValueIs')
                            : t('builder:logicsPanel.text.ifTheOptionIs')}
                        </label>
                      )}
                      <Controller
                        name={`rules.${index}.valueId`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            disabled
                            showArrow={false}
                            placeholder="Option value"
                            value={field.value || null}
                            onChange={(value) => {
                              field.onChange(value);
                              saveRules(field.name, value);
                            }}
                          >
                            {question.options.map((option) => (
                              <Option key={option._id} value={option._id}>
                                {!question?.assignValues
                                  ? option.label
                                  : option?.weight != null
                                  ? option?.weight
                                  : option?.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      colSpan={24}
                      // remove={remove}
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </>
        );
      case 'DROPDOWN':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={11}>
                    <div className="form-group">
                      {index === 0 && (
                        <label
                          title={
                            question?.assignValues
                              ? t('builder:logicsPanel.text.ifTheValueIs')
                              : t('builder:logicsPanel.text.ifTheOptionIs')
                          }
                        >
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {question?.assignValues
                            ? t('builder:logicsPanel.text.ifTheValueIs')
                            : t('builder:logicsPanel.text.ifTheOptionIs')}
                        </label>
                      )}
                      <Controller
                        name={`rules.${index}.valueId`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Select an option"
                            value={field.value || null}
                            onChange={(value) => {
                              field.onChange(value);
                              saveRules(field.name, value);
                            }}
                          >
                            {question.options.map((option) => (
                              <Option key={option._id} value={option._id}>
                                {!question?.assignValues
                                  ? option.label
                                  : option?.weight != null
                                  ? option?.weight
                                  : option?.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </div>
                  </Col>
                  <Col span={13}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      remove={remove}
                      colSpan={18}
                    />
                  </Col>
                </Row>
              </div>
            ))}

            <Button onClick={handleAddRule} icon={<PlusOutlined />}>
              {t('builder:logicsPanel.text.addRule')}
            </Button>
          </>
        );
      case 'CHECKBOX':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={11}>
                    <div className="form-group">
                      {index === 0 && (
                        <label
                          title={
                            question?.assignValues
                              ? t('builder:logicsPanel.text.ifSelectedValuesInclude')
                              : t('builder:logicsPanel.text.ifSelectedOptionsInclude')
                          }
                        >
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {question?.assignValues
                            ? t('builder:logicsPanel.text.ifSelectedValuesInclude')
                            : t('builder:logicsPanel.text.ifSelectedOptionsInclude')}
                        </label>
                      )}
                      <Controller
                        name={`rules.${index}.valueId`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            // value={field?.value?.split(',')}
                            mode="multiple"
                            placeholder={t('builder:logicsPanel.text.selectOptions')}
                            style={{ width: '100%' }}
                            onChange={(value) => {
                              // const concatenatedValue = value.join(',');
                              field.onChange(value);
                              saveRules(field.name, value);
                            }}
                          >
                            {question.options.map((option) => (
                              <Option key={option._id} value={option._id}>
                                {!question?.assignValues
                                  ? option.label
                                  : option?.weight != null
                                  ? option?.weight
                                  : option?.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                      />
                    </div>
                  </Col>
                  <Col span={13}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      remove={remove}
                      colSpan={16}
                    />
                  </Col>
                </Row>
              </div>
            ))}

            <Button onClick={handleAddRule} icon={<PlusOutlined />}>
              {t('builder:logicsPanel.text.addRule')}
            </Button>
          </>
        );
      case 'NUMBER':
      case 'FORMULA':
      case 'RATING':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={14}>
                    <div className="form-group">
                      {index === 0 && (
                        <label title={t('builder:logicsPanel.text.ifTheAnswer')}>
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {t('builder:logicsPanel.text.ifTheAnswer')}
                        </label>
                      )}
                      <Row gutter={8}>
                        <Col span={14}>
                          <Controller
                            name={`rules.${index}.type`}
                            control={control}
                            defaultValue="EQUALS"
                            render={({ field }) => (
                              <Select
                                style={{ width: '100%' }}
                                {...field}
                                onChange={(value) => {
                                  field.onChange(value);
                                  saveRules(field.name, value);
                                }}
                              >
                                <Option value="EQUALS">
                                  {t('builder:logicsPanel.text.isEqual')}
                                </Option>
                                <Option value="IS_GREATER">
                                  {t('builder:logicsPanel.text.isGreaterThan')}
                                </Option>
                                <Option value="IS_LESSER">
                                  {t('builder:logicsPanel.text.isLesserThan')}
                                </Option>
                              </Select>
                            )}
                          />
                        </Col>
                        <Col span={10}>
                          <Controller
                            name={`rules.${index}.value`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <Input
                                type="number"
                                placeholder="Eg. 10"
                                {...field}
                                // style={{ width: '40%' }}
                                onChange={(e) => {
                                  field.onChange(e);
                                  saveRules(field.name, e.target.value);
                                }}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col span={10}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      remove={remove}
                      colSpan={16}
                    />
                  </Col>
                </Row>
              </div>
            ))}

            <Button onClick={handleAddRule} icon={<PlusOutlined />}>
              {t('builder:logicsPanel.text.addRule')}
            </Button>
          </>
        );
      case 'DATE':
        return (
          <>
            {fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 16 }}>
                <Row gutter={8}>
                  <Col span={watchDatePickerMode === 'datetime' ? 16 : 14}>
                    <div className="form-group">
                      {index === 0 && (
                        <label title={t('builder:logicsPanel.text.ifTheDate')}>
                          <PullRequestOutlined style={{ marginRight: 6 }} />
                          {t('builder:logicsPanel.text.ifTheDate')}
                        </label>
                      )}
                      <Row gutter={8}>
                        <Col span={watchDatePickerMode === 'datetime' ? 9 : 12}>
                          <Controller
                            name={`rules.${index}.type`}
                            control={control}
                            defaultValue="EQUALS"
                            render={({ field }) => (
                              <Select
                                style={{ width: '100%' }}
                                {...field}
                                onChange={(value) => {
                                  field.onChange(value);
                                  saveRules(field.name, value);
                                }}
                              >
                                <Option value="EQUALS">
                                  {t('builder:logicsPanel.text.isEqual')}
                                </Option>
                                <Option value="IS_BEFORE">
                                  {t('builder:logicsPanel.text.isBefore')}
                                </Option>
                                <Option value="IS_AFTER">
                                  {t('builder:logicsPanel.text.isAfter')}
                                </Option>
                              </Select>
                            )}
                          />
                        </Col>
                        <Col span={watchDatePickerMode === 'datetime' ? 9 : 12}>
                          <Controller
                            name={`rules.${index}.value`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <DatePicker
                                style={{ width: '100%', height: 32 }}
                                {...field}
                                value={
                                  !field.value
                                    ? null
                                    : !isMoment(field.value)
                                    ? // convert date string formatted as DD-MM-YYYY to moment
                                      moment(field.value, 'DD-MM-YYYY')
                                    : field.value
                                }
                                size="small"
                                format="DD-MM-YYYY"
                                onChange={(date, dateString) => {
                                  field.onChange(dateString);
                                  saveRules(field.name, dateString);
                                }}
                              />
                            )}
                          />
                        </Col>
                        {watchDatePickerMode === 'datetime' && (
                          <Col span={6}>
                            <Controller
                              name={`rules.${index}.valueTime`}
                              control={control}
                              render={({ field }) => (
                                <TimePicker
                                  style={{ width: '100%', height: 32 }}
                                  {...field}
                                  value={
                                    !field.value
                                      ? null
                                      : !isMoment(field.value)
                                      ? moment(field.value, 'HH:mm')
                                      : field.value
                                  }
                                  format="HH:mm"
                                  onChange={(time, timeString) => {
                                    field.onChange(timeString);
                                    saveRules(field.name, timeString);
                                  }}
                                />
                              )}
                            />
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                  <Col span={watchDatePickerMode === 'datetime' ? 8 : 10}>
                    <GoToSection
                      questions={questions}
                      saveChanges={saveRules}
                      index={index}
                      control={control}
                      remove={remove}
                      colSpan={watchDatePickerMode === 'datetime' ? 16 : 16}
                    />
                  </Col>
                </Row>
              </div>
            ))}

            <Button onClick={handleAddRule} icon={<PlusOutlined />}>
              {t('builder:logicsPanel.text.addRule')}
            </Button>
          </>
        );
      default:
        return (
          <Typography.Text>
            {t('builder:logicsPanel.text.noConditionalLogicAvailable')}
          </Typography.Text>
        );
    }
  };

  return renderLogicFields();
};

const GoToSection = ({ questions, saveChanges, index, control, remove, colSpan = 20 }) => {
  const { t } = useTranslation();
  return (
    <div className="form-group">
      {index === 0 && (
        <label title={t('builder:logicsPanel.text.thenGoTo')}>
          <ThunderboltOutlined style={{ marginRight: 6 }} />
          {t('builder:logicsPanel.text.thenGoTo')}
        </label>
      )}
      <Row gutter={8}>
        <Col span={colSpan}>
          <Controller
            name={`rules.${index}.jumpToSection`}
            control={control}
            render={({ field }) => (
              <Select
                style={{ width: '100%' }}
                {...field}
                value={field.value || null}
                placeholder={t('builder:logicsPanel.text.selectAPage')}
                onChange={(value) => {
                  field.onChange(value);
                  saveChanges(field.name, value);
                }}
                notFoundContent={
                  <div
                    className="flex"
                    style={{
                      flexDirection: 'column',
                      alignItems: 'center',
                      minHeight: '150px',
                      textAlign: 'center',
                      gap: 12,
                    }}
                  >
                    <WarningOutlined style={{ fontSize: 24 }} />
                    <Typography.Text type="secondary">
                      No Page found. <br />
                      Create a new Page from <b>Advanced</b> {'>'} <b>Page</b>.
                    </Typography.Text>
                  </div>
                }
              >
                {questions
                  .filter((q) => q.type === 'SECTION')
                  .map((section, idx) => (
                    <Option key={section.questionId} value={section.questionId}>
                      {section.label || `Page ${idx + 1}`}
                    </Option>
                  ))}
                <Option value="END">Submit Form</Option>
              </Select>
            )}
          />
        </Col>
        {remove && (
          <Col>
            <Button
              onClick={() => {
                remove(index);
                saveChanges();
              }}
              icon={<DeleteOutlined />}
              danger
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default LogicsPanel;
